import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { navigate, graphql } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import Wrapper from './wrapper';
import ButtonErrorWrapper from './button-error-wrapper';
import StyledForm from './form-component';
import Button from './button';
import Title from './title';
import Field from './field';
import Intro from './intro';
import ErrorAlert from './alert/error';
import SuccessAlert from './alert/success';
import useCreateSubmission from '../hooks/use-create-submission';

// Use this fragment in your pageQuery
// and use it as <Form {...page.form} />
export const query = graphql`
  fragment StrapiForm on StrapiFormsForms {
    id
    strapiId
    name
    title
    buttonText: button_text
    redirectAfterSuccess: redirect_after_success
    successMessage
    intro: childStrapiFormsFormsIntro {
      childMarkdownRemark {
        html
      }
    }

    components {
      typename: __typename

      ...on StrapiFormsFormsFormsInput {
        id
        name
        label
        placeholder
        required
        type
      }
      ...on StrapiFormsFormsFormsSelect {
        id
        name
        label
        required
        values {
          value
          label
        }
        default_value
        type
      }

      ...on StrapiFormsFormsFormsDate {
        id
        name
        label
        value
        minDaysFromNow: min_days_from_now
        min
        max
        step
        required
        type
      }

      ...on StrapiFormsFormsFormsMessage {
        id
        childStrapiFormsFormsFormsMessageContent {
          childMarkdownRemark {
            html
          }
        }
      }
    }
  }
`;

const Form = ({
  strapiId,
  name,
  title,
  intro,
  buttonText,
  successMessage,
  components = [],
  redirectAfterSuccess = null,
  removeFormWhenSuccess = true,
  successProps = {},
  horizontal = false,
  standout = false,
  buttonProps = {},
  justify,
  className,
  ...props
}) => {
  // allow setting values via a Link
  const { state } = useLocation();
  // remove the key that gatsby always adds
  const { key: _, via: __, ...locationState } = state || {};
  // get all defaultValues ready
  const initialFormFields = (components || []).reduce((acc, field) => {
    if (field.typename === 'StrapiFormsFormsFormsMessage') {
      return acc;
    }
    return ({
      ...acc,
      [field.name]: (field.typename === 'StrapiFormsFormsFormsSelect' ? (field.default_value ?? '') : ''),
    });
  }, {});
  // the initial form state is defaultValues and what is pushed in the location state
  const [formValues, setValues] = useState({
    ...initialFormFields,
    ...locationState,
  });

  const {
    onSubmit,
    data,
    loading,
    error,
  } = useCreateSubmission(formValues, name, strapiId, {
    ctaText: buttonText,
  });

  if (redirectAfterSuccess && data) {
    if (redirectAfterSuccess.startsWith('http')) {
      navigate(redirectAfterSuccess);
      return null;
    }
    const prependSlash = !redirectAfterSuccess.startsWith('/') ? `/${redirectAfterSuccess}` : redirectAfterSuccess;
    const appendSlash = !prependSlash.endsWith('/') ? `${prependSlash}/` : prependSlash;
    navigate(appendSlash);
    return null;
  }

  const showForm = !removeFormWhenSuccess || !(removeFormWhenSuccess && data);

  return (
    <Wrapper className={className} name={name}>
      {title && <Title>{title}</Title>}
      {intro?.childMarkdownRemark?.html && <Intro $content={intro} />}
      {showForm && (
        <StyledForm
          {...props}
          onSubmit={onSubmit}
          $horizontal={horizontal}
          $standout={standout}
        >
          <input type="hidden" name="form-name" value={name} />
          {(components || []).map((field) => (
            <Field
              key={field.id}
              {...field}
              onChange={(e) => {
                setValues((formState) => ({
                  ...formState,
                  [field.name]: e.target.value,
                }));
              }}
              value={formValues[field.name]}
              horizontal={horizontal}
              standout={standout}
            />
          ))}
          <ButtonErrorWrapper $standout={standout} justify={justify}>
            {data && (
              <SuccessAlert
                {...successProps}
                data={data}
                successMessage={successMessage}
                $horizontal={horizontal}
                $standout={standout}
              />
            )}
            {error && (
              <ErrorAlert error={error} />
            )}
            <Button
              type="submit"
              disabled={loading}
              wide={horizontal}
              $horizontal={horizontal}
              $standout={standout}
              {...buttonProps}
            >
              {buttonText}
            </Button>
          </ButtonErrorWrapper>
        </StyledForm>
      )}
      {data && !showForm && (
        <SuccessAlert
          {...successProps}
          data={data}
          successMessage={successMessage}
          $horizontal={horizontal}
          $standout={standout}
        />
      )}
    </Wrapper>
  );
};

export default styled(Form)``;
